import React, { ComponentType } from 'react';

// item 1515 (code splitting) — React.lazy with ONE self-heal. After a deploy, a tab still running
// the old bundle asks for a chunk hash that no longer exists on the server → webpack throws a
// ChunkLoadError (or "Loading CSS chunk … failed") and the route white-screens. On that error we
// stamp sessionStorage and reload the page ONCE (the fresh index.html points at live chunks); if
// the chunk still fails after that reload, the error rethrows into the nearest ErrorBoundary so a
// genuinely broken deploy shows its card instead of reloading forever.
const RELOAD_FLAG = 'basix:chunk-reloaded';

export const isChunkLoadError = (e: unknown): boolean => {
  const err = e as { name?: string; message?: string; code?: string } | null | undefined;
  if (!err) return false;
  if (err.name === 'ChunkLoadError' || err.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  return /Loading (CSS )?chunk [\w-]+ failed/i.test(String(err.message ?? ''));
};

const readFlag = (): boolean => {
  try { return window.sessionStorage.getItem(RELOAD_FLAG) === '1'; } catch { return false; }
};
const writeFlag = (on: boolean) => {
  try {
    if (on) window.sessionStorage.setItem(RELOAD_FLAG, '1');
    else window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch { /* no storage — we just won't be able to limit the retry; still only one per load */ }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyWithRetry = <T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) => React.lazy<T>(async () => {
  try {
    const mod = await factory();
    writeFlag(false); // a chunk loaded fine → the next stale episode may self-heal again
    return mod;
  } catch (e) {
    if (isChunkLoadError(e) && !readFlag()) {
      writeFlag(true);
      window.location.reload();
      // the reload takes over — keep the route suspended (fallback spinner) rather than flashing an error
      return new Promise<{ default: T }>(() => { /* never resolves; the page is reloading */ });
    }
    throw e;
  }
});

export default lazyWithRetry;
